// Pure helpers for grouping a patient's visits by medical vertical and
// deriving the consolidated CTA state for each vertical.
//
// IMPORTANT: this module is intentionally pure — no JSX, no React hooks, no
// window/cookie access. Both the /me/refills page and the /me/history page
// import from here, and keeping it side-effect-free makes the CTA-eligibility
// math (lifted verbatim from the old per-visit renderer) easy to reason about
// and unit-test.

import { addDays, isFuture, isPast } from "date-fns";
import { isEmpty, sortBy } from "lodash";

import {
  ATE_SCO,
  COLOR_KICK_GA_PINK,
  COLOR_KICK_LIGHT_BLUE,
  COLOR_KICK_LIGHT_PINK,
  DB_STATUS_ACTIVE,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_CANCELED,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  DOCTOR_VISIT_STATUS_CLOSED_ERROR,
  DOCTOR_VISIT_STATUS_CLOSED_OTHER,
  DOCTOR_VISIT_STATUS_CLOSED_PAYMENT_UNCOLLECTABLE,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_PRIMARY,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_URGENT,
  DOCTOR_VISIT_STATUS_CLOSED_REFUNDED,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  DOCTOR_VISIT_STATUS_PENDING_PHOTOS,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_READY,
  FILL_PHARMACY_ID_GOGOMEDS,
  FILL_PHARMACY_ID_GOGOMEDS_WEST,
  FILL_PHARMACY_ID_TRUEPILL,
  ORDER_TYPE_PROP_1_PACK,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";

import {
  hasRefillsAvailable,
  isDoctorVisitComplete,
  isNullDate,
  isOriginalOrFollowUpVisit,
  isPharmacyPickupOrderType,
  isScopolamineAddonOrderType,
  latestVisitCompletedAtOrVisitStartedAt,
  numRefillsRemaining,
  refillOrderTypeFor,
} from "~/shared/utils/sharedUtils";

// ---------------------------------------------------------------------------
// Vertical keys + render families
// ---------------------------------------------------------------------------

export const VERTICAL_PERFORMANCE_ANXIETY = "PERFORMANCE_ANXIETY";
export const VERTICAL_INSOMNIA = "INSOMNIA";
export const VERTICAL_GENERALIZED_ANXIETY = "GENERALIZED_ANXIETY";

// Render family controls which CTA/summary logic a vertical uses. Performance
// Anxiety AND Generalized Anxiety both use the "PA" family (matching the old
// code, where GA fell through to ShowPerformanceAnxietyPrescription). Insomnia
// is membership-based with no refill concept.
export const RENDER_FAMILY_PA = "PA";
export const RENDER_FAMILY_INSOMNIA = "INSOMNIA";

const RENDER_FAMILY_BY_KEY = {
  [VERTICAL_PERFORMANCE_ANXIETY]: RENDER_FAMILY_PA,
  [VERTICAL_GENERALIZED_ANXIETY]: RENDER_FAMILY_PA,
  [VERTICAL_INSOMNIA]: RENDER_FAMILY_INSOMNIA,
};

const VERTICAL_LABEL_BY_KEY = {
  [VERTICAL_PERFORMANCE_ANXIETY]: "Performance Anxiety",
  [VERTICAL_GENERALIZED_ANXIETY]: "Generalized Anxiety",
  [VERTICAL_INSOMNIA]: "Sleep",
};

// Clinic badge background colors, drawn from the shared Kick/EMR palette.
const VERTICAL_BADGE_COLOR_BY_KEY = {
  [VERTICAL_PERFORMANCE_ANXIETY]: COLOR_KICK_LIGHT_PINK,
  [VERTICAL_GENERALIZED_ANXIETY]: COLOR_KICK_GA_PINK,
  [VERTICAL_INSOMNIA]: COLOR_KICK_LIGHT_BLUE,
};

export function verticalKeyForVisitType(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return VERTICAL_PERFORMANCE_ANXIETY;
    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return VERTICAL_INSOMNIA;
    case VISIT_TYPE_GENERALIZED_ANXIETY:
    case VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP:
      return VERTICAL_GENERALIZED_ANXIETY;
    default:
      // Unknown visit type → not bucketed (never silently lands in PA).
      return null;
  }
}

// Human-readable clinic label ("Performance Anxiety" / "Sleep" / "Generalized
// Anxiety") for a visit type, or "" when the type isn't recognized.
export function verticalLabelForVisitType(visitType) {
  const key = verticalKeyForVisitType(visitType);
  return key ? VERTICAL_LABEL_BY_KEY[key] : "";
}

// Clinic badge background color for a visit type, or "" when unrecognized.
export function verticalBadgeColorForVisitType(visitType) {
  const key = verticalKeyForVisitType(visitType);
  return key ? VERTICAL_BADGE_COLOR_BY_KEY[key] || "" : "";
}

// order_type (and the equivalent order_item.product_slug) encode the vertical in
// their slug prefix: prop-* = Performance Anxiety, ate-sco-* = Generalized
// Anxiety, sleep-* = Sleep. This is the doctor-mutable signal: when a patient is
// switched to a different vertical mid-visit, order_type/product_slug are updated
// but visit_type is not — so order_type is the source of truth for the badge.
// (scop-addon-* is an addon order-item type only, never a visit's order_type.)
export function verticalKeyForOrderType(orderType) {
  if (!orderType) return null;
  if (orderType.startsWith(ATE_SCO)) return VERTICAL_GENERALIZED_ANXIETY; // "ate-sco"
  if (orderType.startsWith("prop")) return VERTICAL_PERFORMANCE_ANXIETY;
  if (orderType.startsWith("sleep")) return VERTICAL_INSOMNIA;
  return null;
}

// Vertical for a visit: prefer the doctor-mutable order_type, falling back to the
// immutable visit_type when order_type is missing/unrecognized (e.g. legacy rows).
export function verticalKeyForVisit(visit) {
  if (!visit) return null;
  return (
    verticalKeyForOrderType(visit.orderType) ||
    verticalKeyForVisitType(visit.visitType)
  );
}

// Human-readable clinic label for a visit, derived order_type-first. "" when
// neither order_type nor visit_type is recognized.
export function verticalLabelForVisit(visit) {
  const key = verticalKeyForVisit(visit);
  return key ? VERTICAL_LABEL_BY_KEY[key] : "";
}

// Clinic badge background color for a visit, derived order_type-first.
export function verticalBadgeColorForVisit(visit) {
  const key = verticalKeyForVisit(visit);
  return key ? VERTICAL_BADGE_COLOR_BY_KEY[key] || "" : "";
}

// ---------------------------------------------------------------------------
// Display filters (kept verbatim from the old portalPrescriptionRefills.jsx —
// only the call site is centralized here)
// ---------------------------------------------------------------------------

const closedUnpaidVisitStatuses = [
  DOCTOR_VISIT_STATUS_CANCELED,
  DOCTOR_VISIT_STATUS_CLOSED_ERROR,
  DOCTOR_VISIT_STATUS_CLOSED_OTHER,
  DOCTOR_VISIT_STATUS_CLOSED_PAYMENT_UNCOLLECTABLE,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_PRIMARY,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_URGENT,
  DOCTOR_VISIT_STATUS_CLOSED_REFUNDED,
];

const pendingVisitStatuses = [
  DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  DOCTOR_VISIT_STATUS_PENDING_PHOTOS,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
];

// Statuses that genuinely require the patient to DO something — these surface
// in the top-of-page Action Needed region, each with its own CTA.
export const actionNeededVisitStatuses = [
  DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  DOCTOR_VISIT_STATUS_PENDING_PHOTOS,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
];

// Statuses that mean "paid/submitted, waiting on the doctor" — there is no
// patient action, so these render as a calm banner inside the vertical's
// treatment card rather than under Action Needed.
export const inReviewVisitStatuses = [
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_READY,
];

export function isPast45DaysAndPending(visit) {
  return (
    pendingVisitStatuses.includes(visit.status) &&
    isPast(addDays(new Date(visit.createdAt), 45))
  );
}

export function isVisitDisplayable(visit) {
  return (
    !closedUnpaidVisitStatuses.includes(visit.status) &&
    !isPast45DaysAndPending(visit)
  );
}

// ---------------------------------------------------------------------------
// Prescription de-duplication
// ---------------------------------------------------------------------------

// Per-visit dedup (newest first) — drives the visible "current treatment" rows.
// Kept identical to the original so the displayed meds are byte-for-byte the same.
export function deDupedPrescriptions(visit) {
  const prescriptionsByMedName = {};
  const sortedRxs = sortBy(visit.visitPrescriptions, "createdAt").reverse();
  sortedRxs.forEach((rx) => {
    const key = rx.medicationName + rx.medicationFormFactor;
    if (!prescriptionsByMedName[key]) {
      prescriptionsByMedName[key] = rx;
    }
  });
  return sortBy(Object.values(prescriptionsByMedName), "createdAt").reverse();
}

// Cross-visit dedup (newest first) for the /me/history Rx table. Net-new
// surface; does not affect the active-treatment summary.
export function deDupedPrescriptionsForVisits(visits) {
  const all = [];
  (visits || []).forEach((v) => {
    (v.visitPrescriptions || []).forEach((rx) => all.push(rx));
  });
  const byKey = {};
  sortBy(all, "createdAt")
    .reverse()
    .forEach((rx) => {
      const key = rx.medicationName + rx.medicationFormFactor;
      if (!byKey[key]) byKey[key] = rx;
    });
  return sortBy(Object.values(byKey), "createdAt").reverse();
}

// Every active OR canceled prescription across the given visits, newest first, with
// NO de-dup — the source for the /me/history "one row per written prescription"
// table. Canceled prescriptions stay in the log (badged "canceled" in the UI) for
// posterity; only superseded "I" rewrites (e.g. a re-write of an edited order item)
// are excluded so the log shows real authorizations without pre-edit duplicates.
export function allPrescriptionsForVisits(visits) {
  const all = [];
  (visits || []).forEach((v) => {
    // allVisitPrescriptions (active + canceled, minus "I") is the history list. Fall
    // back to the active-only list for payloads that predate that field.
    const rxs =
      v.allVisitPrescriptions && v.allVisitPrescriptions.length
        ? v.allVisitPrescriptions
        : (v.visitPrescriptions || []).filter(
            (rx) => rx && rx.status === DB_STATUS_ACTIVE,
          );
    rxs.forEach((rx) => {
      if (rx) all.push(rx);
    });
  });
  return sortBy(all, "createdAt").reverse();
}

// ---------------------------------------------------------------------------
// Recency helpers (used to order verticals + pick the active visit)
// ---------------------------------------------------------------------------

function visitRecency(visit) {
  return new Date(
    latestVisitCompletedAtOrVisitStartedAt(
      visit.visitDate,
      visit.patientCompletedAt,
    ),
  ).getTime();
}

function sortByRecencyDesc(visits) {
  return [...visits].sort((a, b) => {
    const diff = visitRecency(b) - visitRecency(a);
    if (diff !== 0) return diff;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

// The active visit drives the consolidated summary + CTA cluster. It must equal
// the visit the old code rendered the primary CTA from: the most recent
// completed visit that satisfies the renderer's own guard. Refill-order-type
// visits are never active (isOriginalOrFollowUpVisit) — they feed history only.
function pickActiveVisit(visits, renderFamily) {
  const candidates = visits.filter((v) => {
    if (!isDoctorVisitComplete(v.status)) return false;
    if (isEmpty(v.visitPrescriptions)) return false;
    if (renderFamily === RENDER_FAMILY_PA && !isOriginalOrFollowUpVisit(v.orderType)) {
      return false;
    }
    return true;
  });
  if (candidates.length === 0) return null;
  return sortByRecencyDesc(candidates)[0];
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

/**
 * @param {Object[]} visits  patientRecord.visits
 * @returns {{
 *   verticals: Array<{
 *     key: string,
 *     renderFamily: "PA"|"INSOMNIA",
 *     label: string,
 *     badgeColor: string,       // clinic badge background color
 *     activeVisit: Object|null,
 *     visitHistory: Object[],   // all displayable visits in the bucket, newest first
 *     rxHistory: Object[],      // cross-visit de-duped prescriptions, newest first
 *     rxHistoryAll: Object[],   // every active OR canceled prescription, newest first (no de-dup)
 *   }>,
 *   actionNeeded: Object[],     // cross-vertical, newest first
 * }}
 */
export function groupVisitsByVertical(visits) {
  const displayable = (visits || []).filter(isVisitDisplayable);

  const byKey = {};
  displayable.forEach((visit) => {
    // order_type wins over visit_type: a doctor can switch a patient's product (e.g. to
    // ate-sco, a Generalized Anxiety med) without changing the original visit_type, so the
    // vertical must follow the current order_type. Falls back to visit_type when absent.
    const key = verticalKeyForVisit(visit);
    if (!key) return;
    if (!byKey[key]) {
      byKey[key] = {
        key,
        renderFamily: RENDER_FAMILY_BY_KEY[key],
        label: VERTICAL_LABEL_BY_KEY[key],
        visits: [],
      };
    }
    byKey[key].visits.push(visit);
  });

  const verticals = Object.values(byKey)
    .map((group) => ({
      key: group.key,
      renderFamily: group.renderFamily,
      label: group.label,
      badgeColor: VERTICAL_BADGE_COLOR_BY_KEY[group.key] || "",
      activeVisit: pickActiveVisit(group.visits, group.renderFamily),
      inReviewVisits: sortByRecencyDesc(
        group.visits.filter((v) => inReviewVisitStatuses.includes(v.status)),
      ),
      visitHistory: sortByRecencyDesc(group.visits),
      rxHistory: deDupedPrescriptionsForVisits(group.visits),
      rxHistoryAll: allPrescriptionsForVisits(group.visits),
      _recency: Math.max(...group.visits.map(visitRecency)),
    }))
    // Most-recently-active vertical first.
    .sort((a, b) => b._recency - a._recency);

  // At most one action-needed card per vertical — a patient should never see two
  // "Finish your visit" cards for the same clinic (e.g. two incomplete PA visits).
  // The surviving card represents the most recently CREATED visit in that vertical.
  const actionByVertical = {};
  displayable
    .filter((v) => actionNeededVisitStatuses.includes(v.status))
    .forEach((v) => {
      const key = verticalKeyForVisit(v) || v.id;
      const existing = actionByVertical[key];
      if (
        !existing ||
        new Date(v.createdAt).getTime() > new Date(existing.createdAt).getTime()
      ) {
        actionByVertical[key] = v;
      }
    });
  const actionNeeded = sortByRecencyDesc(Object.values(actionByVertical));

  return { verticals, actionNeeded };
}

// ---------------------------------------------------------------------------
// Autoship (recurring subscription) summary
// ---------------------------------------------------------------------------

/**
 * activeAutoshipForVertical returns the active-autoship summary for a vertical,
 * or null. An autoship counts when its subscription is ACTIVE, maps to the given
 * vertical (by order_type prefix, same rule as visits), and has at least one
 * prescription whose autoshipStatus is ACTIVE with a real (non-zero) next-ship
 * date. nextShipDate is the EARLIEST upcoming ship date across those Rxs.
 *
 * Autoship is sleep-only today, so this surfaces on the Sleep card — but keying
 * it off the vertical means any future autoship-enabled vertical lights up with
 * no extra wiring.
 *
 * @param {Object[]} subscriptions  patientRecord.subscriptions
 * @param {string} verticalKey      VERTICAL_* key for the card
 * @returns {{nextShipDate: string, orderType: string, subscriptionID: string}|null}
 */
export function activeAutoshipForVertical(subscriptions, verticalKey) {
  if (!subscriptions || !verticalKey) return null;
  for (const sub of subscriptions) {
    if (!sub || sub.status !== DB_STATUS_ACTIVE) continue;
    if (verticalKeyForOrderType(sub.orderType) !== verticalKey) continue;
    const shipDates = (sub.prescriptions || [])
      .filter(
        (rx) =>
          rx &&
          rx.autoshipStatus === DB_STATUS_ACTIVE &&
          !isNullDate(rx.autoshipNextShipDate),
      )
      .map((rx) => rx.autoshipNextShipDate);
    if (shipDates.length === 0) continue;
    const nextShipDate = [...shipDates].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    )[0];
    return { nextShipDate, orderType: sub.orderType, subscriptionID: sub.id };
  }
  return null;
}

// ---------------------------------------------------------------------------
// CTA state (eligibility math + button visibility, matching the old
// ShowPerformanceAnxietyPrescription 1:1: a single either/or button — refill
// XOR follow-up — with no button at all while a refill is in flight)
// ---------------------------------------------------------------------------

export function shouldShowUpgradeTo2PackButton(visit) {
  const orderItemsInclude1Pack = visit.orderItems?.length
    ? visit.orderItems.some(
        (orderItem) => orderItem.productSlug === ORDER_TYPE_PROP_1_PACK,
      )
    : visit.orderType == ORDER_TYPE_PROP_1_PACK;
  return orderItemsInclude1Pack;
}

/**
 * isVisitRefillable returns true when the visit's prescriptions can be refilled
 * by mail: the visit is a mail-order (non-pharmacy-pickup) order and ALL of its
 * deduped active prescriptions have refills available — if any is out of
 * refills, expired, canceled, or filled by Truepill/GoGoMeds, the visit is not
 * refillable and the patient needs a follow-up visit instead.
 */
export function isVisitRefillable(visit) {
  if (!visit) return false;
  // Refill eligibility only applies to non-pharmacy-pickup (mail order) orders.
  if (isPharmacyPickupOrderType(visit.orderType)) return false;
  const dedupedRxs = deDupedPrescriptions(visit);
  if (dedupedRxs.length === 0) return false;
  return dedupedRxs.every((rx) => {
    if (!hasRefillsAvailable(rx.refills, rx.numRefillsFilled)) return false;
    if (isPast(addDays(new Date(rx.createdAt), 362)) || rx.refillsCanceled) {
      return false;
    }
    if (
      rx.fillPharmacyID == FILL_PHARMACY_ID_TRUEPILL ||
      rx.fillPharmacyID == FILL_PHARMACY_ID_GOGOMEDS ||
      rx.fillPharmacyID == FILL_PHARMACY_ID_GOGOMEDS_WEST ||
      rx.fillPharmacyID == ""
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Performance Anxiety / Generalized Anxiety CTA state for a vertical, derived
 * from its active visit. Returns null when there is no active visit.
 */
export function computePaCtaState(activeVisit) {
  if (!activeVisit) return null;

  const dedupedRxs = deDupedPrescriptions(activeVisit);
  const prescription = dedupedRxs[0];
  if (!prescription) return null;

  const numRefills = numRefillsRemaining(
    prescription.refills,
    prescription.numRefillsFilled,
  );

  const doRefill = isVisitRefillable(activeVisit);

  const isComplete = activeVisit.status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE;

  // The refill/follow-up button stays available whenever the visit is complete.
  // (A refill shipment in progress is surfaced separately as a "refill on the
  // way" status on the card, so we no longer hide the button while one is in
  // flight — the patient can always reorder.)
  const showCta = isComplete;
  // Pharmacy-pickup orders within 30 days confirm before starting a follow-up.
  const followUpNeedsPharmacyConfirm =
    isPharmacyPickupOrderType(activeVisit.orderType) &&
    isFuture(addDays(new Date(prescription.createdAt), 30));

  return {
    prescription,
    numRefills,
    doRefill,
    showCta,
    followUpNeedsPharmacyConfirm,
    showUpgradeTo2Pack: shouldShowUpgradeTo2PackButton(activeVisit),
  };
}

/**
 * Insomnia CTA state — membership-based, no refill concept.
 */
export function computeInsomniaCtaState(activeVisit) {
  if (!activeVisit) return null;
  return {
    showFollowUpCta:
      activeVisit.status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  };
}

// ---------------------------------------------------------------------------
// Cross-visit refill options
// ---------------------------------------------------------------------------

/**
 * refillableVisitOptions returns every distinct product the patient can refill
 * in a vertical, across ALL of its visits (not just the most recent one),
 * newest first. Each option carries exactly what the refill checkout stamp
 * (setProperVisitCookiesForRefills) consumes, as plain JSON safe for
 * localStorage.
 *
 * Candidates use the same population rules as pickActiveVisit (complete,
 * has active prescriptions, original/follow-up order type). Two visits count
 * as the SAME product when they share a refill order type + flavor
 * (subcategory) — e.g. an original and a follow-up of the same 1-pack
 * collapse to the newest, while commercial vs compounded (or two flavors)
 * stay distinct options. The newest-wins collapse runs BEFORE the
 * isVisitRefillable check so a newest dose that can no longer be refilled
 * (exhausted, canceled, expired, wrong pharmacy) shadows its older same-key
 * siblings — a superseded dose is never offered.
 *
 * @param {Object[]} visits       the vertical's visits (e.g. group.visitHistory)
 * @param {"PA"|"INSOMNIA"} renderFamily  options exist only for the PA family
 * @returns {Array<{
 *   key: string,               // product identity (refill order type + flavor)
 *   refillOrderType: string,
 *   subcategory: string,       // primary item's flavor, "" when none
 *   hasScopAddon: boolean,     // a scop-addon-* order item rides along
 *   orderedAt: string,         // when the visit completed (display only)
 *   visit: {id, orderType, orderItems: Object[]},
 *   prescription: {medicationName: string, medicationDosage: string},
 * }>}
 */
export function refillableVisitOptions(visits, renderFamily) {
  if (renderFamily !== RENDER_FAMILY_PA) return [];

  const candidates = (visits || []).filter((v) => {
    if (!isDoctorVisitComplete(v.status)) return false;
    if (isEmpty(v.visitPrescriptions)) return false;
    return isOriginalOrFollowUpVisit(v.orderType);
  });

  // Newest visit per product key across ALL population candidates, before any
  // refillability check — filtering first would let an older visit of the same
  // product resurface (offering a superseded dose) whenever the newest one has
  // exhausted or canceled refills.
  const newestByKey = {};
  sortByRecencyDesc(candidates).forEach((v) => {
    const primary = (v.orderItems || []).find((oi) => oi && !oi.isAddon);
    // Older visits may not have orderItems — fall back to the visit's orderType.
    const slug = primary?.productSlug || v.orderType;
    const refillOrderType = refillOrderTypeFor(slug) || slug;
    const subcategory = primary?.subcategory || "";
    const key = `${refillOrderType}|${subcategory}`;
    if (newestByKey[key]) return; // newest wins — scan is newest-first
    newestByKey[key] = { v, key, refillOrderType, subcategory };
  });

  // Insertion order follows the newest-first scan (keys are never integer-like).
  return Object.values(newestByKey)
    .filter(({ v }) => isVisitRefillable(v))
    .map(({ v, key, refillOrderType, subcategory }) => {
      // The primary (beta-blocker) prescription, not simply the last-created
      // rx — a Scopolamine patch rx must not become the option's label or the
      // stamped beta-blocker variant.
      const dedupedRxs = deDupedPrescriptions(v);
      const prescription =
        dedupedRxs.find((rx) => rx.medicationFormFactor !== "patch") ||
        dedupedRxs[0];
      return {
        key,
        refillOrderType,
        subcategory,
        hasScopAddon: (v.orderItems || []).some(
          (oi) =>
            oi && oi.isAddon && isScopolamineAddonOrderType(oi.productSlug),
        ),
        orderedAt: latestVisitCompletedAtOrVisitStartedAt(
          v.visitDate,
          v.patientCompletedAt,
        ),
        // Explicit field picks: strips Apollo __typename so the option is plain
        // JSON, and pins the exact shape setProperVisitCookiesForRefills reads.
        visit: {
          id: v.id,
          orderType: v.orderType,
          orderItems: (v.orderItems || []).map((oi) => ({
            id: oi.id,
            consultationType: oi.consultationType,
            productSlug: oi.productSlug,
            subcategory: oi.subcategory,
            isAddon: oi.isAddon,
          })),
        },
        prescription: {
          medicationName: prescription?.medicationName || "",
          medicationDosage: prescription?.medicationDosage || "",
        },
      };
    });
}
